import { type VariantProps, cva } from "class-variance-authority"
import { type JSX, type ReactNode, useState } from "react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip"
import { not } from "~/utils"
import { cn } from "~/utils/style"

const baseStyles = "text-primary antialiased scroll-m-20"

// TODO: Esse componente vai passar por algumas mudanças em relação a nome e variants
// Variants não podem controlar cores ou letter spacing ou como na variant muted e nos headings elas controlaram deverão apenas tamanho.
// Pra isso, criaremos props adicionais e eliminaremos o uso de className, trazendo um design mais consistente.

const typographyVariants = cva(baseStyles, {
  variants: {
    variant: {
      h1: "text-4xl tracking-tight",
      h2: "text-3xl tracking-tight",
      h3: "text-2xl tracking-tight",
      h4: "text-xl tracking-tight",
      h5: "text-lg tracking-tight",
      h6: "text-base tracking-tight",
      p: "text-base",
      small: "text-sm",
      bodyXs: "text-xs",
      muted: "text-sm text-muted-foreground",
    },
    fontWeight: {
      thin: "font-thin",
      extraLight: "font-extralight",
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    textAlign: {
      start: "text-start",
      center: "text-center",
      end: "text-end",
    },
  },
  defaultVariants: {
    variant: "p",
    textAlign: "start",
    fontWeight: "normal",
  },
})

interface TypographyProps extends VariantProps<typeof typographyVariants> {
  children: ReactNode
  as?: keyof JSX.IntrinsicElements
  className?: string
  truncateWidth?: number
}

const defaultTypographyElement = "p"

export function Typography({
  children,
  variant,
  fontWeight,
  textAlign,
  as = defaultTypographyElement,
  className,
  truncateWidth,
}: TypographyProps) {
  const Component = as

  const [isOverflowing, setIsOverflowing] = useState(false)

  if (truncateWidth) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger
            asChild
            style={{
              maxWidth: truncateWidth,
            }}
            onMouseLeave={() => setIsOverflowing(false)}
            onMouseEnter={event => {
              const element = event.currentTarget
              const hasOverflow = element.scrollWidth > element.clientWidth
              if (hasOverflow && not(isOverflowing)) {
                setIsOverflowing(element.scrollWidth > element.clientWidth)
              }
            }}
          >
            <Component
              className={cn(
                typographyVariants({ variant, fontWeight, textAlign }),
                className,
                "truncate cursor-default",
              )}
            >
              {children}
            </Component>
          </TooltipTrigger>

          {isOverflowing && (
            <TooltipContent collisionPadding={16} side="top" align="center">
              <Component className={cn("break-all")}>{children}</Component>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Component
      className={cn(
        typographyVariants({ variant, fontWeight, textAlign }),
        className,
      )}
    >
      {children}
    </Component>
  )
}
